import React, { useState } from "react";
import { Badge, Button, Card, Col, Modal, Row, Spinner } from "react-bootstrap";
import { Invitation, InvitationStatus } from "@features/invitations/types/invitation";
import StatusBadge from "@features/invitations/components/StatusBadge";
import { useMyInvitations } from "@features/user/invitations/hooks/useMyInvitations";
import { useMyInviteActions } from "@features/user/invitations/hooks/useMyInviteActions";
import { InvitationsCardProps } from "@features/invitations/types/invitationsCardProps";
import { accentFor } from "@features/user/invitations/utils/invitationCardAccent";
import { prettyDate } from "@features/user/invitations/utils/formatDateTime";
import NoResult from "@core/components/NoResult";
import { useNavigate } from "react-router-dom";



/**
 * Renders a list of invitation cards for the current user, allowing them to accept or reject invitations.
 *
 * - Fetches invitations using `useMyInvitations` and handles loading and empty states.
 * - Provides actions to accept or reject invitations via `useMyInviteActions`.
 * - Displays a confirmation modal before rejecting an invitation.
 * - Handles UI feedback for loading and confirmation states.
 *
 * @component
 * @returns {JSX.Element} The rendered invitation cards and confirmation modal.
 */
const InvitationsCards: React.FC<InvitationsCardProps> = () => {
    const navigate = useNavigate();
    const [page] = useState(1);
    const { data, loading, reload } = useMyInvitations({ page });
    const { doAccept, doReject } = useMyInviteActions();


    // confirm modal state
    const [confirmState, setConfirmState] = useState<{ id: number } | null>(null);
    const [confirming, setConfirming] = useState(false);

    // close modal if not confirming
    const closeConfirm = () => {
        if (!confirming) setConfirmState(null);
    };

    // confirm reject action
    const confirmReject = async () => {
        // guard
        if (!confirmState) return;

        try {
            // set loading state
            setConfirming(true);

            await doReject(confirmState.id);

            // close modal and reload data
            setConfirmState(null);
            await reload();

            // If no more invitations, navigate to dashboard
            if (data.length === 1) {
                navigate('/entities');
            }
        } finally {
            // reset loading state
            setConfirming(false);
        }
    };

    // loading state
    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" />
            </div>
        );
    }

    // empty state
    if (!loading && data.length === 0) {
        return (
            <>
                <NoResult line_1="No pending invitations right now." line_2="We'll show new invitations here as they arrive." />
            </>
        );
    }

    return (
        <>
            <Row xs={1} sm={2} lg={3} className="g-3" data-testid="invitations-cards-row">
                {data.map((inv: Invitation) => {
                    const id = inv.invitation_id;
                    const status = (inv.invite_status_name || "").toLowerCase() as InvitationStatus;
                    const accent = accentFor(status);

                    return (
                        <Col key={id} data-testid={`invitation-card-col-${id}`}>
                            <div className={`rounded-3 border-start border-5 ${accent}`} data-testid={`invitation-card-border-${id}`}>
                                <Card className="h-100 shadow-sm border-0" data-testid={`invitation-card-${id}`}>
                                    <Card.Body className="d-flex flex-column">
                                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                            <div className="me-auto">
                                                <div className="fw-semibold text-break">{inv.scope_ref_name} - {inv.department_name}</div>
                                                <div
                                                    className="text-muted small text-truncate"
                                                    title={inv.profile_description || "—"}
                                                    data-testid={`invitation-profile-description-${id}`}
                                                >
                                                    {inv.profile_description || "—"}
                                                </div>
                                            </div>
                                            <StatusBadge status={status} />
                                        </div>

                                        <div className="text-muted small mb-3" data-testid={`invitation-invited-by-${id}`}>
                                            Invited by <span className="fw-semibold text-break">{inv.invited_by_name || "—"}</span>
                                        </div>

                                        <div className="mt-auto d-flex flex-wrap align-items-center gap-3">
                                            <div className="small text-muted" data-testid={`invitation-expires-${id}`}>
                                                Expires{" "}
                                                <Badge bg="light" text="dark">
                                                    {prettyDate(inv.expires_at)}
                                                </Badge>
                                            </div>

                                            <div className="d-flex gap-2 w-100 w-md-auto">
                                                <Button
                                                    size="sm"
                                                    variant="success"
                                                    className="flex-fill flex-md-grow-0"
                                                    onClick={async () => {
                                                        await doAccept(id);
                                                        await reload();
                                                        // If no more invitations, navigate to dashboard
                                                        if (data.length === 1) {
                                                            navigate('/entities');
                                                        }
                                                    }}
                                                    data-testid={`invitation-accept-btn-${id}`}
                                                >
                                                    Accept
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-danger"
                                                    className="flex-fill flex-md-grow-0"
                                                    onClick={() => setConfirmState({ id })}
                                                    data-testid={`invitation-reject-btn-${id}`}
                                                >
                                                    Reject
                                                </Button>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        </Col>
                    );
                })}
            </Row>

            <Modal
                show={!!confirmState}
                onHide={closeConfirm}
                centered
                backdrop="static"
                keyboard={!confirming}
                data-testid="reject-confirm-modal"
            >
                <Modal.Header closeButton={!confirming} data-testid="reject-confirm-modal-header">
                    <Modal.Title>Reject Invitation</Modal.Title>
                </Modal.Header>

                <Modal.Body data-testid="reject-confirm-modal-body">
                    Are you sure you want to reject this invitation? This action cannot be undone.
                </Modal.Body>

                <Modal.Footer data-testid="reject-confirm-modal-footer">
                    <Button
                        variant="outline-secondary"
                        onClick={closeConfirm}
                        disabled={confirming}
                        data-testid="reject-confirm-no-btn"
                    >
                        No
                    </Button>
                    <Button
                        variant="danger"
                        onClick={confirmReject}
                        disabled={confirming}
                        data-testid="reject-confirm-yes-btn"
                    >
                        {confirming ? "Rejecting…" : "Yes, reject"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default InvitationsCards;
